import { endOfDay, subDays } from 'date-fns';

/**
 * Client mirror of the server's `ANALYTICS_MAX_RANGE_DAYS` default
 * (see src/app/api/crm/trpc/services/analytics/analytics.types.ts, #TAG-1556).
 *
 * The server rejects any analytics date range wider than that cap (750 days by
 * default) to stop an unbounded aggregation scan from starving the DB pool. The
 * "All time" filter preset, however, stores an epoch→far-future sentinel
 * (`new Date(0)` … year 2099) which is used purely for its UI label + calendar
 * default — that sentinel is ~55 years wide and therefore trips the guard on
 * EVERY funnel-analytics request (400 "Date range too large").
 *
 * We keep the sentinel in UI state (so the "All time" label keeps working) and
 * clamp only at the query boundary via `clampAnalyticsRange`. Kept 1 day under
 * the server default as a rounding-safety margin (endOfDay vs the raw cap).
 */
export const ANALYTICS_MAX_RANGE_DAYS = 749;

/**
 * The "all time" window sentinel — epoch → far-future (year 2099), matching the
 * "All time" filter preset described above. It is intentionally wider than the
 * server cap; always pass it through `clampAnalyticsRange` before sending it to
 * the analytics API. Module-level constants so the values are stable instants
 * (no queryKey churn when used inline in render).
 */
export const ALL_TIME_START = new Date(0);
export const ALL_TIME_END = new Date('2099-12-31T23:59:59.999Z');

/**
 * Clamp a [from, to] range so the request sent to the analytics API never
 * exceeds the server cap:
 *  - `to` is pulled back to end-of-today (a far-future sentinel would otherwise
 *    point the window at an empty date range and return zero rows), and
 *  - `from` is pushed forward so the span is at most ANALYTICS_MAX_RANGE_DAYS.
 *
 * Output is stable within a calendar day (endOfDay/subDays round to fixed
 * instants), so calling this inline in render does not churn the tRPC queryKey.
 */
export function clampAnalyticsRange(from: Date, to: Date): { from: Date; to: Date } {
    const cap = endOfDay(new Date());
    const boundedTo = to.getTime() > cap.getTime() ? cap : to;
    const minFrom = subDays(boundedTo, ANALYTICS_MAX_RANGE_DAYS);
    const boundedFrom = from.getTime() < minFrom.getTime() ? minFrom : from;
    return { from: boundedFrom, to: boundedTo };
}
